<script setup lang="ts" generic="T">
import { Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { type Component, computed, inject, nextTick, onMounted, onUnmounted, provide, ref } from "vue";

import DropTarget from "~/components/dumb/DropTarget.vue";
import ModalInner from "~/components/dumb/ModalInner.vue";
import Template from "~/components/dumb/Template.vue";
import MobileNavigationSwipe from "~/components/MobileNavigationSwipe.vue";
import { DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION, DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION } from "~/constants/injection";
import MODAL_WIDTH_MAP from "~/constants/modal";
import { colorsByTheme } from "~/constants/style";
import { IconMode, ModalPosition, ModalWidth } from "~/shared/enums";
import { useAppStore, usePageStore } from "~/stores";
import { useMobileSheetSwipe } from "~/utils/swipe";
import useModalOpenTracking from "~/utils/useModalOpenTracking";

const TRANSITION_RIGHT = {
  enter: "ease-in-out duration-150",
  enterFrom: "translate-x-full",
  enterTo: "translate-x-0",
  leave: "ease-in-out duration-150",
  leaveFrom: "translate-x-0",
  leaveTo: "translate-x-full",
};
const TRANSITION_LEFT = {
  enter: "ease-in-out duration-150",
  enterFrom: "-translate-x-full",
  enterTo: "translate-x-0",
  leave: "ease-in-out duration-150",
  leaveFrom: "translate-x-0",
  leaveTo: "-translate-x-full",
};
const TRANSITION_NORMAL = {
  enter: "duration-150 ease-out",
  enterFrom: "opacity-0 scale-95",
  enterTo: "opacity-100 scale-100",
  leave: "duration-150 ease-in",
  leaveFrom: "opacity-100 scale-100",
  leaveTo: "opacity-0 scale-95",
};
const TRANSITION_MOBILE_SWIPE = {
  enter: "ease-in-out duration-150",
  enterFrom: "translate-y-full",
  enterTo: "translate-y-0",
  leave: "ease-in-out duration-150",
  leaveFrom: "translate-y-0",
  leaveTo: "translate-y-full",
};

const TouchDialogPanel = DialogPanel as unknown as new () => {
  $props: InstanceType<typeof DialogPanel>["$props"] & {
    onTouchstart?: (event: TouchEvent) => void;
    onTouchmove?: (event: TouchEvent) => void;
    onTouchend?: (event: TouchEvent) => void;
  };
};

const props = defineProps<{
  entity: T;
  title: string | ((t: T) => string);
  id?: string;
  hideTitle?: boolean;
  emphasizeTitle?: boolean;
  description?: string | ((t: T) => string);
  width: ModalWidth;
  position?: ModalPosition;
  iconMode?: IconMode;
  closeText?: string;
  customStyles?: string;
  panelStyles?: string;
  customWidthPixels?: number;
  customHeightPixels?: number;
  closeIconStyles?: string;
  titleIcon?: Component;
  titleIconArgs?: object | ((t: T) => object);
  hFull?: boolean;
  backdropBlur?: boolean;
  preventBackdropClose?: boolean;
  overflowClip?: boolean;
  selected?: boolean;
  noFocus?: boolean;
  focusPanelInitially?: boolean;
  dropDisabledOverComponents?: HTMLElement[];
  dataTestid?: string;
  mobileNoSwipe?: boolean;
  mobileFullscreen?: boolean;
  noPadding?: boolean;
  hidden?: boolean;
  overflowHidden?: boolean;
  shouldUpdateKeyboardHeight?: boolean;
  transparentOverlay?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  afterLeave: [];
  keydown: [event: KeyboardEvent];
  drop: [files: File[]];
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const parentOnAfterOpen = inject(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION, null);
const parentOnAfterClose = inject(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION, null);

const isHidden = ref(false);

provide(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  if (!pageStore.isMobile) {
    return;
  }
  isHidden.value = true;
  parentOnAfterOpen?.();
});
provide(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  if (!pageStore.isMobile) {
    return;
  }
  isHidden.value = false;
  parentOnAfterClose?.();
});

const keyboardHeight = ref(0);

const focusRef = ref<HTMLDivElement | null>(null);

const colors = computed(() => colorsByTheme[pageStore.theme]);
const modalWrapperRef = ref<HTMLElement | null>(null);
const positionLeft = computed(() => props.position === ModalPosition.LEFT);
const positionRight = computed(() => props.position === ModalPosition.RIGHT);
const positionTop = computed(() => props.position === ModalPosition.TOP);
const positionBottom = computed(
  () =>
    props.position === ModalPosition.BOTTOM ||
    (pageStore.isMobile && (props.position === ModalPosition.RIGHT || props.position === undefined))
);
const isMobileBottomSheet = computed(() => pageStore.isMobile && positionBottom.value);
const isFullHeightSidebar = computed(() => positionLeft.value && props.noPadding);

const widthStyles = computed(() => {
  if (props.mobileFullscreen && pageStore.isMobile) {
    return props.overflowHidden ? { height: "98vh" } : { height: "100%", maxHeight: "98vh" };
  }

  if (pageStore.isMobile && props.position !== ModalPosition.TOP && props.position !== ModalPosition.LEFT) {
    return { maxWidth: "100%" };
  }
  const width = props.customWidthPixels ?? MODAL_WIDTH_MAP[props.width];
  if (width === undefined) {
    return {} as Record<string, string>;
  }
  return { maxWidth: `${width}px` };
});

const transformStyle = computed(() =>
  pageStore.isMobile && positionBottom && props.shouldUpdateKeyboardHeight && keyboardHeight.value > 0
    ? `translateY(-${keyboardHeight.value}px)`
    : undefined
);

const customHeightStyles = computed(() => {
  if (props.customHeightPixels) {
    return { height: `${props.customHeightPixels}px`, maxHeight: `${props.customHeightPixels}px` };
  }
  return {};
});

const shadowClass = computed(() =>
  props.transparentOverlay ? "shadow-2xl border border-md shadow-opposite/10" : "shadow-xl"
);

const hasMobileSwipe = computed(() => !props.mobileNoSwipe && pageStore.isMobile);

const mobileSheetSwipe = hasMobileSwipe.value ? useMobileSheetSwipe(() => emit("close")) : undefined;

const onClose = () => {
  if (props.preventBackdropClose) {
    return;
  }
  emit("close");
};

const updateKeyboardHeight = () => {
  const currentHeight = window.visualViewport?.height || window.innerHeight;
  const initialHeight = window.innerHeight;
  const heightDiff = initialHeight - currentHeight;
  keyboardHeight.value = heightDiff > 150 ? heightDiff : 0;
};
onMounted(() => {
  if (pageStore.isMobile && props.shouldUpdateKeyboardHeight) {
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateKeyboardHeight);
    } else {
      window.addEventListener("resize", updateKeyboardHeight);
    }
  }
});
onUnmounted(() => {
  if (pageStore.isMobile && props.shouldUpdateKeyboardHeight) {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", updateKeyboardHeight);
    } else {
      window.removeEventListener("resize", updateKeyboardHeight);
    }
  }
});

useModalOpenTracking(
  () => !!props.entity,
  modalWrapperRef,
  () => {
    appStore.isModalOpen = props.id !== "mobile-leftbar";

    if (!props.noFocus) {
      return;
    }
    nextTick(() => focusRef.value?.focus());
  }
);
</script>

<template>
  <TransitionRoot appear :show="Boolean(entity)" as="template" @after-leave="emit('afterLeave')">
    <Dialog
      as="div"
      class="relative z-10 h-full transition-opacity duration-150"
      :class="{ 'opacity-0': hidden || isHidden }"
      :open="Boolean(entity)"
      :initial-focus="focusPanelInitially ? focusRef : undefined"
      @close="onClose">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <DialogOverlay
          class="fixed inset-0"
          :class="{
            'bg-scrim': !transparentOverlay,
            'bg-transparent': transparentOverlay,
            'backdrop-blur-[3px]': backdropBlur && !transparentOverlay,
          }" />
      </TransitionChild>

      <div
        ref="modalWrapperRef"
        class="dart-modal-wrapper fixed inset-0"
        :class="[
          !overflowClip && !positionRight && !pageStore.isMobile && 'overflow-y-auto',
          isMobileBottomSheet ? 'safe-area-bottom-sheet-wrapper' : !isFullHeightSidebar && 'p-safe-area',
        ]"
        :style="{
          '--background': colors.bgStd,
          '--highlight': colors.highlight,
          transform: transformStyle,
        }"
        @keydown="emit('keydown', $event)">
        <div
          class="relative flex h-full flex-col items-center"
          :class="[
            mobileFullscreen && pageStore.isMobile
              ? 'pt-10'
              : positionBottom
                ? pageStore.isMobile
                  ? 'justify-end px-0 pb-0'
                  : 'justify-end px-1 pb-2.5'
                : positionTop
                  ? 'justify-start px-1 pt-2.5 pb-1 sm:px-3 sm:pb-3'
                  : 'justify-evenly px-1 pt-10 pb-1 sm:p-10',
          ]">
          <TransitionChild
            as="template"
            v-bind="
              positionLeft
                ? TRANSITION_LEFT
                : positionRight
                  ? TRANSITION_RIGHT
                  : hasMobileSwipe
                    ? TRANSITION_MOBILE_SWIPE
                    : TRANSITION_NORMAL
            ">
            <TouchDialogPanel
              :id="id"
              class="w-full rounded-lg"
              :style="[
                widthStyles,
                customHeightStyles,
                panelStyles,
                mobileSheetSwipe?.sheetStyles?.value,
                isMobileBottomSheet && transformStyle ? { paddingBottom: '0px' } : undefined,
              ]"
              :data-testid="dataTestid"
              :class="{
                [shadowClass]: !customHeightPixels,
                'bg-std': isMobileBottomSheet || isFullHeightSidebar,
                'safe-area-bottom-sheet-panel !rounded-2xl !rounded-b-none': isMobileBottomSheet,
                'safe-area-sidebar-panel !rounded-none': isFullHeightSidebar,
                'absolute inset-y-2': positionLeft || positionRight,
                'left-2': positionLeft,
                '!inset-y-0 !left-0': positionLeft && noPadding,
                'right-2': positionRight,
                relative: !positionLeft && !positionRight,
                'h-full': hFull,
                'select-none': !selected,
                'transition-all': !positionLeft && !pageStore.isMobile,
                'justify-start': customHeightPixels,
              }"
              @touchstart="mobileSheetSwipe?.onTouchstart($event)"
              @touchmove="mobileSheetSwipe?.onTouchmove($event)"
              @touchend="mobileSheetSwipe?.onTouchend()">
              <div v-if="noFocus || focusPanelInitially" ref="focusRef" tabindex="-1" class="sr-only" />

              <DropTarget
                v-if="$slots.dragOverlay"
                :disabled-over-components="dropDisabledOverComponents"
                styles="rounded-lg"
                class="flex flex-col"
                :class="{
                  [shadowClass]: customHeightPixels,
                  'max-h-full': customHeightPixels,
                }"
                @drop="emit('drop', $event)">
                <template #main>
                  <ModalInner
                    :entity="entity"
                    :title="title"
                    :hide-title="hideTitle"
                    :emphasize-title="emphasizeTitle"
                    :description="description"
                    :position="position"
                    :icon-mode="iconMode"
                    :close-text="closeText"
                    :custom-styles="customStyles"
                    :close-icon-styles="closeIconStyles"
                    :title-icon="titleIcon"
                    :title-icon-args="titleIconArgs"
                    :width-style="widthStyles"
                    :has-mobile-swipe="hasMobileSwipe"
                    :overflow-hidden="overflowHidden"
                    @close="emit('close')">
                    <template #default="{ entity: slotEntity }">
                      <slot :entity="slotEntity" />
                    </template>
                    <template v-if="$slots.footerLeft" #footerLeft="{ entity: slotEntity }">
                      <slot name="footerLeft" :entity="slotEntity" />
                    </template>
                    <template v-if="$slots.actions" #actions="{ entity: slotEntity }">
                      <slot name="actions" :entity="slotEntity" />
                    </template>
                  </ModalInner>
                </template>
                <template #dragOverlay>
                  <slot name="dragOverlay" />
                </template>
              </DropTarget>
              <Template v-else>
                <ModalInner
                  :entity="entity"
                  :title="title"
                  :hide-title="hideTitle"
                  :emphasize-title="emphasizeTitle"
                  :description="description"
                  :position="position"
                  :icon-mode="iconMode"
                  :close-text="closeText"
                  :custom-styles="customStyles"
                  :close-icon-styles="closeIconStyles"
                  :title-icon="titleIcon"
                  :title-icon-args="titleIconArgs"
                  :width-style="widthStyles"
                  :has-mobile-swipe="hasMobileSwipe"
                  :overflow-hidden="overflowHidden"
                  :class="{
                    [shadowClass]: customHeightPixels,
                    'max-h-full': customHeightPixels,
                  }"
                  @close="emit('close')">
                  <template #default="{ entity: slotEntity }">
                    <slot :entity="slotEntity" />
                  </template>
                  <template v-if="$slots.footerLeft" #footerLeft="{ entity: slotEntity }">
                    <slot name="footerLeft" :entity="slotEntity" />
                  </template>
                  <template v-if="$slots.actions" #actions="{ entity: slotEntity }">
                    <slot name="actions" :entity="slotEntity" />
                  </template>
                </ModalInner>
              </Template>

              <MobileNavigationSwipe v-if="pageStore.isMobile" />
            </TouchDialogPanel>
          </TransitionChild>
          <div />
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
.safe-area-bottom-sheet-wrapper {
  padding-top: env(safe-area-inset-top, 0px);
}

.safe-area-bottom-sheet-panel {
  padding: 0 env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
}

.safe-area-sidebar-panel {
  padding: env(safe-area-inset-top, 0px) 0 env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
}

.dart-modal-wrapper::-webkit-scrollbar {
  background: var(--background);
}
</style>
